import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Dialog, 
  DialogContent, 
  MenuItem, 
  Select, 
  FormControl,
  InputLabel,
  SelectChangeEvent,
  TextField,
  Grid,
  Button,
  DialogActions
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import Dropdown from '../../Components/DropDown/drop-down.tsx';
import { getItems, editItems, editOrders } from './action.ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
interface Item {
  id: number;
  name: string;
  quantity: number;
  price: number;
  status: string; 
}

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  onRefresh?: () => void;
  paymentMode: string;
  amountCash:string;
  amountUpi:string;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ 
  open, 
  onClose, 
  orderId, 
  onRefresh,
  paymentMode,
  amountCash,
  amountUpi
}) => {
  const [items, setItems] = useState<Item[]>([]);
  const [originalItems, setOriginalItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [upiAmount, setUpiAmount] = useState<number>(0);
  const [originalCashAmount, setOriginalCashAmount] = useState<number>(0);
  const [originalUpiAmount, setOriginalUpiAmount] = useState<number>(0);
  const [hasChanges, setHasChanges] = useState(false);
  const paymentType = Number(paymentMode) === 1 ? 'cod' : 'prepaid';
  const deliveredItemsCount = items.filter(item => item.status === 'Delivered').length;

  useEffect(() => {
    if (open) {
      fetchItems();
      setCashAmount(0);
      setUpiAmount(0);
      setOriginalCashAmount(0);
      setOriginalUpiAmount(0);
      setHasChanges(false);
    }
  }, [open, orderId]);

  const fetchItems = () => {
    setLoading(true);
    setError(null);
    
    getItems(
      10,
      0, 
      orderId,
      (response) => {
        const transformedItems = response.message.item_details_list.map((item: any) => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.invoice_per_item,
          status: item.status || 'order created'
        }));
        setItems(transformedItems);
        setOriginalItems([...transformedItems]);
        setLoading(false);
        setRefreshing(false);
      },
      (errMessage) => {
        setError(errMessage);
        setLoading(false);
        setRefreshing(false);
      }
    );
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchItems();
  };

  const handleStatusChange = (itemId: number, newStatus: string) => {
    const updatedItems = items.map(item => 
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    setItems(updatedItems);
    
    const hasItemChanges = JSON.stringify(updatedItems) !== JSON.stringify(originalItems);
    const hasPaymentChanges = cashAmount !== originalCashAmount || upiAmount !== originalUpiAmount;
    setHasChanges(hasItemChanges || hasPaymentChanges);
  };
  const handleCashAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setCashAmount(0);
    } else if (/^\d+$/.test(value)) {
      setCashAmount(Number(value));
    }
    
    const hasItemChanges = JSON.stringify(items) !== JSON.stringify(originalItems);
    const hasPaymentChanges = (value === '' ? 0 : Number(value)) !== originalCashAmount || upiAmount !== originalUpiAmount;
    setHasChanges(hasItemChanges || hasPaymentChanges);
  };

  const handleUpiAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setUpiAmount(0);
    } else if (/^\d+$/.test(value)) {
      setUpiAmount(Number(value));
    }
    const hasItemChanges = JSON.stringify(items) !== JSON.stringify(originalItems);
    const hasPaymentChanges = cashAmount !== originalCashAmount || (value === '' ? 0 : Number(value)) !== originalUpiAmount;
    setHasChanges(hasItemChanges || hasPaymentChanges);
  };

  const handleSave = () => {
    const updatePromises = items.map(item => {
      if (item.status !== originalItems.find(i => i.id === item.id)?.status) {
        return new Promise((resolve, reject) => {
          editItems(
            item.id,
            item.status,
            (response) => resolve(response),
            (errMessage) => reject(errMessage)
          );
        });
      }
      return Promise.resolve();
    });

    Promise.all(updatePromises)
      .then(() => {
        const allItemsCount = items.length;
        const deliveredItemsCount = items.filter(item => item.status === 'Delivered').length;
        const undeliveredItemsCount = items.filter(item => item.status === 'Undelivered').length;
        
        let newOrderStatus = '';
        if (deliveredItemsCount === allItemsCount) {
          newOrderStatus = 'Delivered';
        } else if (undeliveredItemsCount === allItemsCount) {
          newOrderStatus = 'Attempted';
        } else if (deliveredItemsCount > 0 || undeliveredItemsCount > 0) {
          newOrderStatus = 'Partially Delivered';
        } else {
          newOrderStatus = 'Order Created';
        }
        
        const paymentData = paymentType === 'cod' ? {
          cash_amount: cashAmount > 0 ? cashAmount : null,
          upi_amount: upiAmount > 0 ? upiAmount : null
        } : {};
        
        editOrders(
          orderId,
          null,
          null,
          newOrderStatus,
          null,
          paymentData.cash_amount,
          paymentData.upi_amount,
          (response) => {
            toast.success('Order updated successfully');
            setOriginalItems([...items]);
            setOriginalCashAmount(cashAmount);
            setOriginalUpiAmount(upiAmount);
            setHasChanges(false);
            
            if (onRefresh) onRefresh();
            
            if (deliveredItemsCount + undeliveredItemsCount === allItemsCount) {
              setTimeout(() => onClose(), 1000);
            }
          },
          (error) => {
            toast.error(error.message);
          }
        );
      })
      .catch(error => {
        toast.error(`Error updating items: ${error}`);
      });
  };
  const handleCancel = () => {
    setItems([...originalItems]);
    setCashAmount(originalCashAmount);
    setUpiAmount(originalUpiAmount);
    setHasChanges(false);
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const totalCollected = cashAmount + upiAmount;
  const balanceAmount = totalAmount - totalCollected;

  const orderStatus = [
    {id:'order created',  label:'Order Created'},
    {id:'Delivered', label:'Delivered'},
    {id:'Undelivered', label:'Undelivered'},
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ marginLeft: { sm: '-180px' } }}>
        <Heading title={`Order Details`} closeIcon="true" onClose={onClose} />
      </Box>
      <DialogContent sx={{padding:'10px 15px'}}>
        {loading ? (
          <Typography>Loading items...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Items:</Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '4px', mt: 1 }}>
              {items.map((item) => (
                <Box key={item.id} display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom="1px solid #eee">
                  <Typography fontWeight="bold" sx={{ width: '30%' }}>
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ width: '30%', textAlign: 'center' }}>
                    {item.quantity} x ₹{item.price} = ₹{item.quantity * item.price}
                  </Typography>
                  <Box sx={{ width: '40%', minWidth: 120 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={item.status}
                        onChange={(e: SelectChangeEvent) => handleStatusChange(item.id, e.target.value)}
                        displayEmpty
                      >
                        {orderStatus.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between" p={2} sx={{ backgroundColor: '#f5f5f5' }}>
                <Typography fontWeight="bold">Total:</Typography>
                <Typography fontWeight="bold">₹{totalAmount}</Typography>
              </Box>
            </Box>

            {paymentType === 'cod' && deliveredItemsCount > 0 && (
              <Box sx={{ mt: 3, p: 2, border: '1px solid #ddd', borderRadius: '4px' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Payment Collection (COD)
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Cash Amount (₹)"
                      type="number"
                      value={ cashAmount || amountCash}
                      onChange={handleCashAmountChange}
                      inputProps={{ min: 0, max: totalAmount }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="UPI Amount (₹)"
                      type="number"
                      value={upiAmount || amountUpi}
                      onChange={handleUpiAmountChange}
                      inputProps={{ min: 0, max: totalAmount }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Total Collected: ₹{totalCollected}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={balanceAmount === 0 ? 'success.main' : 'error.main'}
                  >
                    Balance Amount: ₹{balanceAmount}
                  </Typography>
                  {balanceAmount < 0 && (
                    <Typography variant="caption" color="error">
                      Warning: Collected amount exceeds total amount!
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={!hasChanges}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          disabled={!hasChanges}
          variant="contained" 
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailsModal;
